import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Upload } from "lucide-react";
import { useContract } from "@/hooks/useContract";
import { useLanguage } from "@/context/LanguageContext";
import { adminLabels } from "@/i18n";
import { Pagination } from "@/components/ui/pagination";
import { ContractUploadDialog } from "@/components/dialogs/ContractUploadDialog";
import { ContractList } from "./ContractList";
import ContractPopup from "@/components/orders/ContractPopup";
import { ExtendedContract } from "@/types/payment";

type ContractManagementLabels = {
  title?: string;
  description?: string;
  uploadContract?: string;
  loadingContracts?: string;
  errorLoadingContracts?: string;
  contractsCount?: string;
  searchPlaceholder?: string;
  search?: string;
  clear?: string;
  allStatuses?: string;
  statusDraft?: string;
  statusSigned?: string;
  statusCompleted?: string;
  statusCancelled?: string;
};

const ContractManagement: React.FC = () => {
  const { language } = useLanguage();
  const cm = (adminLabels(language).economy?.contractManagement ??
    {}) as ContractManagementLabels;
  const { useGetAllContractsExtended, updateContractAsync } = useContract();
  const [page, setPage] = useState(1);
  const [limit] = useState(50);

  // // Fetch extended contracts with all details
  // const {
  //   data: contractsData,
  //   isLoading: contractsLoading,
  //   error: contractsError,
  //   refetch: refetchContractsData,
  // } = useGetAllContractsExtended(page, limit);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [appliedFilters, setAppliedFilters] = useState<{
    status?: string;
    search?: string;
  }>({});
  const [shouldSearch, setShouldSearch] = useState(false);
  const [selectedContract, setSelectedContract] =
    useState<ExtendedContract | null>(null);

  // Fetch extended contracts with all details and filters
  const {
    data: contractsData,
    isLoading: contractsLoading,
    error: contractsError,
    refetch: refetchContractsData,
  } = useGetAllContractsExtended(
    page,
    limit,
    shouldSearch ? appliedFilters.status : undefined,
    shouldSearch ? appliedFilters.search : undefined,
  );

  const handleSearch = () => {
    const newFilters: { status?: string; search?: string } = {};

    if (filterStatus !== "all") {
      newFilters.status = filterStatus;
    }

    if (search.trim()) {
      newFilters.search = search.trim();
    }

    setAppliedFilters(newFilters);
    setShouldSearch(true);
    setPage(1); // Reset to first page when searching
  };

  const handleClearFilters = () => {
    setFilterStatus("all");
    setSearch("");
    setAppliedFilters({});
    setShouldSearch(false);
    setPage(1);
  };

  // Use server-side filtered data directly
  const filteredContracts = contractsData?.contracts || [];

  // Check if search button should be disabled
  const isSearchDisabled = filterStatus === "all" && !search.trim();

  const handleUploadContract = () => {
    setUploadOpen(true);
  };

  if (contractsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">{cm.loadingContracts}</div>
      </div>
    );
  }

  if (contractsError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">{cm.errorLoadingContracts}</div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold">{cm.title}</h1>
            <p className="text-muted-foreground">{cm.description}</p>
          </div>
          <Button onClick={handleUploadContract} className="gap-2">
            <Upload className="h-4 w-4" />
            {cm.uploadContract}
          </Button>
        </div>

        <Card className="glass-card">
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <CardTitle>
                {cm.contractsCount} ({filteredContracts.length})
              </CardTitle>
              <div className="flex flex-col md:flex-row md:items-center gap-4 w-full md:w-auto mt-2 md:mt-0">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="border bg-card rounded px-2 py-1 w-full md:w-auto"
                >
                  <option value="all">{cm.allStatuses}</option>
                  <option value="draft">{cm.statusDraft}</option>
                  <option value="signed">{cm.statusSigned}</option>
                  <option value="completed">{cm.statusCompleted}</option>
                  <option value="cancelled">{cm.statusCancelled}</option>
                </select>
                <input
                  type="text"
                  placeholder={cm.searchPlaceholder}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="border bg-card rounded px-2 py-1 w-full md:w-64"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={handleSearch}
                    size="sm"
                    disabled={isSearchDisabled}
                  >
                    {cm.search}
                  </Button>
                  {(appliedFilters.status || appliedFilters.search) && (
                    <Button
                      onClick={handleClearFilters}
                      variant="outline"
                      size="sm"
                    >
                      {cm.clear}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ContractList
              contracts={filteredContracts}
              onViewContract={(contract) => {
                setSelectedContract(contract);
              }}
            />
            {contractsData && (
              <Pagination
                currentPage={page}
                totalPages={Math.ceil(contractsData.total / limit)}
                totalItems={contractsData.total}
                itemsPerPage={limit}
                onPageChange={setPage}
                onItemsPerPageChange={(newLimit) => {
                  setPage(1);
                }}
                itemsPerPageOptions={[10, 25, 50]}
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Contract Upload Dialog */}
      <ContractUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onSuccess={() => {
          // Refresh the contract list
          refetchContractsData();
        }}
      />

      {/* Contract Popup */}
      {selectedContract && (
        <ContractPopup
          contract={selectedContract}
          onClose={() => setSelectedContract(null)}
          isAdmin={true}
        />
      )}
    </>
  );
};

export default ContractManagement;
